import { useState } from "react";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import {
  Badge,
  Callout,
  Card,
  CopyButton,
  DefinitionList,
  Empty,
  ErrorState,
  Field,
  Loading,
  Modal,
  PageIntro,
  formatDateTime,
  useToast,
} from "../components/ui.jsx";
import { IconKey, IconPlus, IconRefresh } from "../components/icons.jsx";

export default function Tenants() {
  const toast = useToast();
  const { data, error, loading, reload } = useAsync(() => api.listTenants(), []);
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState(null);
  const [detail, setDetail] = useState(null);

  const tenants = data ?? [];

  async function toggleActive(tenant) {
    try {
      await api.setTenantActive(tenant.Alias, !tenant.Active);
      reload();
      toast.success(tenant.Active ? "Tenant deactivated" : "Tenant activated");
    } catch (err) {
      toast.error("Could not change the tenant", err.message);
    }
  }

  async function rotate(tenant) {
    try {
      const result = await api.rotateTenantSecret(tenant.Alias);
      setSecret({ alias: tenant.Alias, value: result.secret, rotated: true });
    } catch (err) {
      toast.error("Could not rotate the secret", err.message);
    }
  }

  return (
    <>
      <PageIntro title="Tenants on the platform">
        The head-end is the system of record for tenants. It allocates the identifiers that
        must be unique across the whole estate (utility prefix, supply group code and radio
        slot), then pushes the record to the management system. Those values cannot be
        changed afterwards without orphaning every token already issued.
      </PageIntro>

      <div className="toolbar">
        <span className="spacer" />
        <button className="btn btn-secondary btn-sm" onClick={reload}>
          <IconRefresh /> Refresh
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
          <IconPlus /> New tenant
        </button>
      </div>

      {loading ? (
        <Loading label="Loading tenants" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : tenants.length === 0 ? (
        <Card>
          <Empty
            title="No tenants yet"
            action={
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                <IconPlus /> Create the first tenant
              </button>
            }
          >
            A tenant is a RESCO or utility operating on the platform. Devices are issued to
            a tenant before they can be commissioned.
          </Empty>
        </Card>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Alias</th>
                <th>Name</th>
                <th>Utility prefix</th>
                <th>Supply group</th>
                <th>Radio slot</th>
                <th>Status</th>
                <th className="cell-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.Alias}>
                  <td>
                    <button className="btn btn-ghost btn-sm mono" onClick={() => setDetail(t)}>
                      {t.Alias}
                    </button>
                  </td>
                  <td>{t.Name}</td>
                  <td className="mono">{t.UtilityPrefix}</td>
                  <td className="mono">{t.SupplyGroupCode}</td>
                  <td className="mono">{t.RFFrequencySlot}</td>
                  <td>
                    <Badge kind={t.Active ? "success" : "neutral"}>
                      {t.Active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="cell-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => rotate(t)}>
                      <IconKey /> Rotate secret
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(t)}>
                      {t.Active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateTenant
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setSecret({ alias: result.tenant.Alias, value: result.secret });
            reload();
          }}
        />
      )}

      {secret && <SecretModal secret={secret} onClose={() => setSecret(null)} />}

      {detail && (
        <Modal title={detail.Name} hint={detail.Alias} onClose={() => setDetail(null)} wide>
          <DefinitionList
            items={[
              ["Alias", <span className="mono">{detail.Alias}</span>],
              ["Contact email", detail.Email],
              ["Phone", detail.Phone || null],
              ["Utility prefix", <span className="mono">{detail.UtilityPrefix}</span>],
              ["Supply group code", <span className="mono">{detail.SupplyGroupCode}</span>],
              ["Tariff index", detail.TariffIndex],
              ["Key revision", `#${detail.KeyRevision}`],
              ["Encryption", detail.EncryptionAlgorithm],
              ["Radio slot", detail.RFFrequencySlot],
              ["Created", formatDateTime(detail.CreatedAt)],
            ]}
          />
        </Modal>
      )}
    </>
  );
}

function CreateTenant({ onClose, onCreated }) {
  const toast = useToast();
  const [form, setForm] = useState({
    alias: "",
    name: "",
    email: "",
    phone: "",
    supply_group_code: "",
    tariff_index: 1,
  });
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState(null);

  const aliasValid = /^[a-z][a-z0-9-]{2,29}$/.test(form.alias);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setFieldError(null);
    try {
      const result = await api.createTenant({
        ...form,
        tariff_index: Number(form.tariff_index),
      });
      onCreated(result);
      toast.success("Tenant created", `${result.tenant.Name} is ready.`);
    } catch (err) {
      setFieldError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Create a tenant"
      hint="Identifiers are allocated automatically and cannot be changed later"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            form="create-tenant"
            disabled={busy || !aliasValid || !form.name || !form.email}
          >
            {busy ? <span className="spinner" /> : null}
            Create tenant
          </button>
        </>
      }
    >
      <form id="create-tenant" onSubmit={submit}>
        {fieldError && <Callout kind="warning">{fieldError}</Callout>}

        <Field
          label="Alias"
          required
          error={form.alias && !aliasValid ? "Three to thirty characters, starting with a letter, using only lower-case letters, digits and hyphens." : null}
          hint="Used as a subdomain and as a message topic, so it cannot be changed later."
        >
          <input
            className="input mono"
            value={form.alias}
            onChange={(e) => setForm({ ...form, alias: e.target.value.toLowerCase() })}
            placeholder="acme-power"
            required
          />
        </Field>

        <Field label="Organisation name" required>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Acme Power Limited"
            required
          />
        </Field>

        <div className="field-row">
          <Field label="Contact email" required>
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </Field>
          <Field label="Phone">
            <input
              className="input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Field>
        </div>

        <div className="field-row">
          <Field
            label="Supply group code"
            hint="Leave blank to derive one from the allocated prefix."
          >
            <input
              className="input mono"
              value={form.supply_group_code}
              onChange={(e) => setForm({ ...form, supply_group_code: e.target.value })}
              placeholder="Automatic"
            />
          </Field>
          <Field label="Tariff index" hint="Must be 0 to 15; it feeds key derivation.">
            <input
              className="input"
              type="number"
              min="0"
              max="15"
              value={form.tariff_index}
              onChange={(e) => setForm({ ...form, tariff_index: e.target.value })}
            />
          </Field>
        </div>

        <Callout kind="info">
          The utility prefix and radio slot are allocated by the head-end so they stay
          unique across every tenant. The API secret is shown once, immediately after
          creation.
        </Callout>
      </form>
    </Modal>
  );
}

function SecretModal({ secret, onClose }) {
  return (
    <Modal
      title={secret.rotated ? "New API secret" : "Tenant created"}
      hint={secret.alias}
      onClose={onClose}
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          I have saved it
        </button>
      }
    >
      <Callout kind="warning">
        This secret is shown once and cannot be retrieved again; only its hash is stored.
        {secret.rotated && " The previous secret keeps working for 24 hours so the management system is not locked out mid-rotation."}
      </Callout>

      <div className="secret-reveal">
        <span>{secret.value}</span>
        <CopyButton value={secret.value} />
      </div>

      <p className="hint" style={{ marginTop: 12 }}>
        Give this to the management system. It authenticates as the tenant over HTTP Basic,
        using the alias as the username.
      </p>
    </Modal>
  );
}
